import React from 'react'
import { number, object } from 'yup'
import { Trans, useTranslation } from 'react-i18next'
import { DataWithUpload, Draft, FloorplanData } from 'zens-store'
import { StyleSheet, ViewProps } from 'react-native'
import { observer } from 'mobx-react'
import { Button, Spacing, Text } from 'zens-storybook'

import { Form, FormProps } from '..'

const styles = StyleSheet.create({
  form: {
    width: 360,
  },
  left: {
    marginRight: Spacing.XS,
  },
  right: {
    marginLeft: Spacing.XS,
  },
  scale: {
    maxWidth: 360,
  },
})

interface Props extends Omit<FormProps, 'draft' | 'config' | 'schema' | 'buttons'> {
  draft: Draft<FloorplanData>
  onSave?: (draft: Draft<DataWithUpload<FloorplanData>>) => Promise<void>
  onCancel?: (draft: Draft<FloorplanData>) => Promise<void>
  onDelete?: (draft: Draft<FloorplanData>) => Promise<void>
  onError?: (errors?: Record<string, string>) => void
}
const FloorForm = (props: Props) => {
  const { draft, errors, onSave, onCancel, onDelete, style, ...otherProps } = props
  const { t } = useTranslation()
  function resetScale() {
    draft.update('floorplan.scale', null)
  }

  const config = {
    groups: [
      {
        fields: [
          {
            key: 'number',
            label: t('Floor level'),
          },
          {
            key: 'name',
            label: t('Floor name'),
            optional: true,
          },
        ],
      },
      ...(draft.data.floorplan?.background
        ? [
            {
              fields: [
                {
                  key: 'floorplan.scale.distance',
                  label: t('Scale'),
                  left: (
                    <Text style={styles.left}>
                      <Trans>Line length</Trans>
                    </Text>
                  ),
                  right: (
                    <>
                      <Text style={styles.right}>
                        <Trans>cm</Trans>
                      </Text>
                      <Button
                        light
                        left="pencil"
                        title={t('Redo')}
                        style={styles.right}
                        onPress={resetScale}
                        disabled={!draft.data.floorplan?.scale?.end && !draft.data.floorplan?.scale?.start}
                        testID="FloorForm.ResetScale"
                      />
                    </>
                  ),
                  style: styles.scale,
                },
              ],
            },
          ]
        : []),
    ],
  }
  const schema = object({
    number: number().required(t('Floor level is required')),
    ...(draft.data.floorplan?.background
      ? {
          floorplan: object({
            scale: object({
              distance: number().required(t('Scale line length is required')).typeError(t('Invalid line length')),
              start: object({
                x: number().required(t('Scale line start is required')),
                y: number().required(t('Scale line start is required')),
              }),
              end: object({
                x: number().required(t('Scale line end is required')),
                y: number().required(t('Scale line end is required')),
              }),
            }).nullable(),
          }),
        }
      : undefined),
  })
  const buttons = [
    [
      {
        title: t('Save'),
        onPress: onSave,
        validate: true,
        contentContainerStyle: {
          minWidth: 120,
        },
      },
      {
        title: t('Cancel'),
        onPress: onCancel,
        light: true,
        contentContainerStyle: {
          minWidth: 120,
        },
      },
    ],
    ...(onDelete
      ? [
          {
            onPress: onDelete,
            left: 'trash' as const,
            danger: true,
            contentContainerStyle: {
              minWidth: 40,
            },
          },
        ]
      : []),
  ]

  return (
    <Form
      draft={draft}
      config={config}
      schema={schema}
      buttons={buttons}
      style={[styles.form, style]}
      errors={errors}
      {...otherProps}
    />
  )
}

interface SkeletonProps extends ViewProps {}
const Skeleton = (props: SkeletonProps) => {
  const { style, ...otherProps } = props
  const { t } = useTranslation()
  const config = {
    groups: [
      {
        fields: [
          {
            key: 'number',
            label: t('Floor level'),
          },
          {
            key: 'name',
            label: t('Floor name'),
          },
        ],
      },
    ],
  }
  return <Form.Skeleton config={config} style={[styles.form, style]} numberOfbuttons={2} {...otherProps} />
}
FloorForm.Skeleton = Skeleton

export default observer(FloorForm)
